import { FC, useCallback, useEffect, useState } from 'react';

import { Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { useNavigate, useParams } from 'react-router-dom';
import { getWebHookRoute } from 'routes/events/list';
import { Button, Form, Label, Link, TextArea, Typography, message } from 'ui';

import { useLazyGetEventDetailQuery } from 'services/events/eventsApiService';
import {
  useCreateWebHookMutation,
  useLazyGetWebHookQuery,
  useUpdateWebHookMutation,
} from 'services/promoter/promoterApiService';
import { setActiveEvent } from 'slices/eventsSlice';
import { useAppDispatch } from 'store/index';

import EventsLoadingState from 'containers/events-loading-state';

import { EventCreatedType } from 'types/events/eventTypes';
import { HookType, WebHook } from 'types/promoters/promoterTypes';

import CustomEmail from '../custom-email';
import NotifyUserOfOrder from '../notify-user-of-order';
import SaveContact from '../save-contact';
import s from './WebHookContainter.module.scss';

const defaultHookTypes = [
  {
    value: HookType.notifyUserOfOrder,
    label: 'Notify User of Order',
  },
  {
    value: HookType.customEmail,
    label: 'Custom Email',
  },
  {
    value: HookType.saveContact,
    label: 'Save Contact',
  },
];

const WebHookContainer: FC = () => {
  const [form] = useForm();
  const { eventId, hookId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [createWebHook, { isLoading: creating }] = useCreateWebHookMutation();
  const [updateWebHook, { isLoading: updating }] = useUpdateWebHookMutation();

  const [getWebHook, { isFetching }] = useLazyGetWebHookQuery();

  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();

  const [hookType, setHookType] = useState<HookType>(HookType.notifyUserOfOrder);
  const [editHook, setEditHook] = useState<WebHook>();

  const [fields, setFields] = useState<
    {
      caption: string;
      value: string;
    }[]
  >();

  const content = {
    notifyUserOfOrder: <NotifyUserOfOrder />,
    customEmail: <CustomEmail />,
    saveContact: <SaveContact fields={fields} setFields={setFields} />,
  };

  const handleGetEditingWebHook = useCallback(
    async (hookId: string) => {
      const res = await getWebHook({ hookId: hookId });

      if ('data' in res) {
        setEditHook(res?.data?.webhook);
      }
    },
    [setEditHook, getWebHook],
  );

  useEffect(() => {
    if (hookId !== 'create' && hookId) {
      handleGetEditingWebHook(hookId);
    }
  }, [handleGetEditingWebHook, hookId]);

  const handleGetEditingEvent = useCallback(
    async (eventId: string) => {
      const res = await getEventDetail(eventId);

      if ('data' in res) {
        dispatch(setActiveEvent(res.data as EventCreatedType));
      }
    },
    [dispatch, getEventDetail],
  );
  useEffect(() => {
    if (eventId) {
      handleGetEditingEvent(eventId);
    }
  }, [eventId, handleGetEditingEvent]);

  useEffect(() => {
    if (editHook) {
      setHookType(editHook.webHookType);
      const dynamicFields = editHook.settings.reduce((acc, item) => {
        acc[item.type] = item.value;
        return acc;
      }, {});

      const fieldsToSaveSetting = editHook.settings.find((s) => {
        return s.type === 'fieldsToSave';
      });

      if (fieldsToSaveSetting) {
        try {
          const parsed = JSON.parse(fieldsToSaveSetting.value); // { FFFF: "FIRSTNAME", TTTT: "NOOFTICKETS" }

          const formatted = Object.entries(parsed).map(([caption, value]) => ({
            caption,
            value: value as string,
          }));

          setFields(formatted);
        } catch (error) {
          console.error('Invalid JSON in fieldsToSave', error);
        }
      }

      const initialTickets = editHook.ticketTypes.map((item) => item.id);

      form.setFieldsValue({
        description: editHook.description,
        active: editHook.active,
        webHookType: editHook.webHookType,
        ticketTypes: initialTickets,
        ...dynamicFields,
      });
    }
  }, [editHook, form]);

  const handleSubmit = useCallback(
    async (data) => {
      let settings;

      if (data.webHookType === HookType.saveContact && !fields) {
        message.warning('Please specify fields to save.');
        return;
      }
      const fieldsToSaveResult: Record<string, string> | undefined = fields?.reduce((acc, item) => {
        acc[item.caption] = item.value;
        return acc;
      }, {} as Record<string, string>);

      if (data.webHookType === HookType.notifyUserOfOrder) {
        settings = [{ type: 'emailToNotify', value: data.emailToNotify }];
      } else if (data.webHookType === HookType.customEmail) {
        settings = [
          { type: 'emailSubject', value: data.emailSubject },
          { type: 'emailTemplate', value: data.emailTemplate },
          { type: 'emailToNotify', value: data.emailToNotify },
        ];
      } else if (data.webHookType === HookType.saveContact) {
        settings = [
          { type: 'contactProvider', value: data.contactProvider },
          { type: 'fieldsToSave', value: JSON.stringify(fieldsToSaveResult ?? '') },
        ];
      }

      const tempTicketTypes = data?.ticketTypes?.map((item) => ({
        ticketTypeId: item.toLowerCase(),
      }));

      const result = {
        ...data,
        eventId: eventId,
        ticketTypes: tempTicketTypes,
        settings: settings,
      };

      if (hookId === 'create') {
        const res = await createWebHook(result);

        if ('data' in res) {
          message.success('Web hook created successfully.');
          navigate(getWebHookRoute(eventDetail?.eventId ?? ''));
        }
      } else if (hookId !== 'create' && hookId !== undefined) {
        const res = await updateWebHook({ id: hookId, data: result });

        if ('data' in res) {
          message.success('Web hook updated successfully.');
          navigate(getWebHookRoute(eventDetail?.eventId ?? ''));
        }
      }
    },
    [fields, eventId, hookId, navigate, eventDetail?.eventId, updateWebHook, createWebHook],
  );

  return (
    <div className={s.wrapper}>
      <Link
        to={getWebHookRoute(eventDetail?.eventId ?? '')}
        icon="arrowLeft"
        label="Web Hooks"
        labelType="h6"
        className={s.backLink}></Link>
      <div className={s.header}>
        <div className={s.titileHeader}>
          {eventDetail && (
            <Typography type="h2" weight={700}>
              &quot;{eventDetail?.basicInfo?.eventName ?? ''}&quot;&nbsp;Web hooks
            </Typography>
          )}
        </div>

        {/*<Breadcrumbs routes={BREADCRUMBS_ROUTES} className={s.breadcrumbs} />*/}
      </div>
      {isFetching ? (
        <div className={s.emptyWrapper}>
          <EventsLoadingState size={240} />
        </div>
      ) : (
        <Form form={form} onFinish={handleSubmit}>
          <div style={{ width: '100%' }}>
            <Label>Web Hook Type</Label>
            <Form.Item name="webHookType" initialValue={HookType.notifyUserOfOrder}>
              <Select
                style={{ width: '100%', height: 40 }}
                onChange={(value) => setHookType(value as HookType)}
                options={defaultHookTypes?.map((item) => ({
                  label: item.label,
                  value: item.value,
                }))}
                value={hookType}
              />
            </Form.Item>
          </div>

          <div className={s.row}>
            <div className={s.item}>
              <Label required>Description</Label>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: 'Description is required' },
                  { min: 5, message: 'Description must be at least 5 characters' },
                ]}>
                <TextArea style={{ width: '100%' }} name="description" size="middle" />
              </Form.Item>
            </div>
          </div>
          <div className={s.row}>
            <div className={s.item}>
              <Label>Web Hook Active</Label>
              <Form.Item name="active" initialValue={true}>
                <Select
                  style={{ width: '100%', height: 40 }}
                  options={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                />
              </Form.Item>
            </div>
            <div className={s.item}>
              <Label>Ticket Type Restrictions</Label>
              <Form.Item name="ticketTypes">
                <Select
                  mode="multiple"
                  style={{ width: '100%', height: 40 }}
                  options={eventDetail?.ticketTypes?.map((item) => ({
                    label: item.name,
                    value: item.id?.toLowerCase(),
                  }))}
                />
              </Form.Item>
            </div>
          </div>

          {content[hookType]}
          <Button
            htmlType="submit"
            loading={creating || updating}
            style={{ marginTop: 24, width: 200 }}>
            {hookId === 'create' ? 'CREATE' : 'SAVE'}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default WebHookContainer;
